import type {CalypsoCatalog} from "#pricing/catalogs/calypso/catalog"
import {createPriceContainer, createVariantConditionedPriceNodes} from "#pricing/catalogs/graph-helpers"
import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import {MeshAmountNode} from "#pricing/nodes/mesh-amount-node"
import type {MeshesNode} from "#pricing/nodes/meshes-node"
import type {PriceContainer} from "#pricing/nodes/price-container"

/**
 * Both door styles are triggered by their mesh, the finish by the furniture finish variant. Each range offers only a
 * subset of the catalog finishes, so the caller passes its finish map, the number of finishes that remain per door style
 * and the finishes to drop. A door style is left unpriced by omitting its count.
 */
export function createForinoWallUnitContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    finishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
    expectedFinishCounts: {fluted?: number; smooth?: number},
    finishExclude?: string[],
): PriceContainer {
    const flutedAmount = new MeshAmountNode({parameters: {name: "5841 - Forino Wall Unit 300mm fluted", meshes: Meshes}})
    const flutedUnits =
        expectedFinishCounts.fluted === undefined
            ? []
            : createVariantConditionedPriceNodes(catalog, "5841", expectedFinishCounts.fluted, finishConfigMap, flutedAmount, finishExclude)

    const smoothAmount = new MeshAmountNode({parameters: {name: "7830 - Forino 2 Door Wall Unit 300mm smooth", meshes: Meshes}})
    const smoothUnits =
        expectedFinishCounts.smooth === undefined
            ? []
            : createVariantConditionedPriceNodes(catalog, "7830", expectedFinishCounts.smooth, finishConfigMap, smoothAmount, finishExclude)

    return createPriceContainer("Forino Wall Unit", "Forino Wall Unit", [...flutedUnits, ...smoothUnits], true)
}

/** Triggered by its mesh, the finish by the furniture finish variant. The catalog lists it in the six Brenta finishes. */
export function createBrentaWallUnitContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    finishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
): PriceContainer {
    const wallUnitAmount = new MeshAmountNode({parameters: {name: "4724 - Brenta Wall Unit 350mm", meshes: Meshes}})
    const wallUnitUnits = createVariantConditionedPriceNodes(catalog, "4724", 6, finishConfigMap, wallUnitAmount)

    return createPriceContainer("Brenta Wall Unit", "Brenta Wall Unit", wallUnitUnits, true)
}

/** Triggered by its mesh, the finish by the furniture finish variant. The catalog lists it in the three satin finishes. */
export function createDiamanteTallWallUnitContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    finishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
): PriceContainer {
    const tallWallUnitAmount = new MeshAmountNode({parameters: {name: "5670 - Diamante Tall Wall Unit", meshes: Meshes}})
    const tallWallUnitUnits = createVariantConditionedPriceNodes(catalog, "5670", 3, finishConfigMap, tallWallUnitAmount)

    return createPriceContainer("Diamante Tall Wall Unit", "Diamante Tall Wall Unit", tallWallUnitUnits, true)
}

/**
 * Triggered by its mesh (added by the "Tall Wall Unit" and "All Units" options), the finish by the furniture finish variant. The catalog
 * lists it in the three Petra finishes. Handles and legs are priced separately.
 */
export function createPetraTallWallUnitContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    finishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
): PriceContainer {
    const tallWallUnitAmount = new MeshAmountNode({parameters: {name: "5852 - Petra Tall Wall Unit 470 x 370 x 1230mm Fluted", meshes: Meshes}})
    const tallWallUnitUnits = createVariantConditionedPriceNodes(catalog, "5852", 3, finishConfigMap, tallWallUnitAmount)

    return createPriceContainer("Petra Tall Wall Unit", "Petra Tall Wall Unit", tallWallUnitUnits, true)
}
